import React from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { followOAuthRedirect, useDecideOAuthConsent, useOAuthConsent } from 'api/oauthServer';
import { extractApiError } from 'api/errors';
import { formatDate } from 'i18n/format';

const PROOF_ERRORS = { invalid_password: 'oauth.invalidPassword', invalid_code: 'oauth.invalidCode' };

function decisionError(t, error) {
  const key = PROOF_ERRORS[error?.response?.data?.code];
  return key ? t(key) : extractApiError(error, t('oauth.failed'));
}

export default function OAuthConsentPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const requestId = params.get('request');
  const preview = useOAuthConsent(requestId);
  const decision = useDecideOAuthConsent(requestId);
  const [redirectError, setRedirectError] = React.useState(false);
  const [leaving, setLeaving] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const consent = preview.data;
  const decide = async (approve) => {
    setRedirectError(false);
    try {
      const proof = { current_password: password, ...(consent.mfa_required ? { code } : {}) };
      const result = await decision.mutateAsync({ approve, consentToken: consent.consent_token, proof });
      setLeaving(true);
      followOAuthRedirect(result.redirect_uri);
    } catch (_) { setLeaving(false); setRedirectError(true); }
  };
  if (!requestId) return <Alert severity="error">{t('oauth.expired')}</Alert>;
  if (preview.isError) {
    const notPermitted = preview.error?.response?.data?.code === 'not_permitted';
    return <Alert severity="error">{t(notPermitted ? 'oauth.notPermitted' : 'oauth.expired')}</Alert>;
  }
  if (preview.isLoading) return <CircularProgress aria-label={t('oauth.loading')} />;
  if (!consent) return null;
  const busy = decision.isPending || leaving;
  const proven = Boolean(password) && (!consent.mfa_required || Boolean(code));
  return <Paper sx={{ p: { xs: 2.5, sm: 4 }, maxWidth: 640, mx: 'auto' }}>
    <Stack component="form" spacing={2.5} onSubmit={(event) => { event.preventDefault(); if (proven && !busy) decide(true); }}>
      <Typography component="h1" variant="h5">{t('oauth.title')}</Typography>
      <Typography>{t('oauth.request', { client: consent.client_name })}</Typography>
      <Alert severity="warning">
        {t('oauth.unverified', { date: formatDate(consent.client_registered_at, { dateStyle: 'medium', timeStyle: 'short' }) })}
      </Alert>
      <Box>
        <Typography variant="body2" color="text.secondary">{t('oauth.project')}</Typography>
        <Typography fontWeight={700}>{consent.tenant_name}</Typography>
        <Typography variant="caption" color="text.secondary">{t('oauth.switchProject')}</Typography>
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{t('oauth.permissions')}</Typography>
        <Box component="ul" sx={{ pl: 2.5 }}>
          {consent.scopes.map((scope) => <Typography component="li" key={scope.key}>
            {scope.key === 'mcp:read' ? t('oauth.mcpRead') : scope.description}
          </Typography>)}
        </Box>
      </Box>
      <Alert severity="info">{t('oauth.recipient', { origin: consent.redirect_origin })}</Alert>
      <Typography variant="body2" color="text.secondary">{t(consent.mfa_required ? 'oauth.proofMfa' : 'oauth.proof')}</Typography>
      <TextField label={t('oauth.currentPassword')} type="password" autoComplete="current-password" value={password}
        onChange={(event) => setPassword(event.target.value)} disabled={busy} />
      {consent.mfa_required && <TextField label={t('oauth.code')} autoComplete="one-time-code" value={code}
        onChange={(event) => setCode(event.target.value)} disabled={busy} inputProps={{ maxLength: 32 }} />}
      {(decision.isError || redirectError) && <Alert severity="error">{decisionError(t, decision.error)}</Alert>}
      <Stack direction="row" spacing={1.5}>
        <Button type="submit" variant="contained" disabled={busy || !proven}>{t('oauth.allow')}</Button>
        <Button variant="outlined" disabled={busy} onClick={() => decide(false)}>{t('oauth.deny')}</Button>
      </Stack>
    </Stack>
  </Paper>;
}
